import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

/**
 * A reusable SEO component that injects standard meta tags,
 * Open Graph tags, and Twitter Cards into the document head.
 * 
 * Props can be passed directly, or it can fall back to using i18n
 * translation keys.
 * 
 * @param {string} title - The page title (will have " | EMA-X solutions" appended). If not provided, falls back to `t(translationKey + '.title')`
 * @param {string} description - The page description. If not provided, falls back to `t(translationKey + '.description')`
 * @param {string} keywords - The page keywords. If not provided, falls back to `t(translationKey + '.keywords')`
 * @param {string} image - The URL of the image to use for og:image. Default is '/og-image.webp'
 *   IMPORTANT: og:image must be JPEG or PNG. WhatsApp and Facebook crawlers do
 *   not reliably support WebP for link previews, even though Telegram/Instagram do.
 *   Keep this image under ~300 KB or WhatsApp will drop the preview entirely.
 * @param {string} translationKey - A base key for i18n, e.g., 'seo.home'.
 */
const SEO = ({ title, description, keywords, image = '/og-image.webp', translationKey }) => {
  const { t, i18n } = useTranslation();

  // Determine actual values from props or translations
  const actualTitle = title || (translationKey ? t(`${translationKey}.title`, { defaultValue: '' }) : '');
  const actualDescription = description || (translationKey ? t(`${translationKey}.description`, { defaultValue: '' }) : '');
  const actualKeywords = keywords || (translationKey ? t(`${translationKey}.keywords`, { defaultValue: '' }) : '');
  
  // Base constants
  const siteName = 'EMA-X solutions';
  const fullTitle = actualTitle ? `${actualTitle} | ${siteName}` : siteName;
  const currentLang = i18n.language || 'ar';
  
  // We can construct the current URL safely
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://www.emaxsolution.com';
  let currentUrl = typeof window !== 'undefined' ? window.location.href : siteUrl;
  
  // Force canonicals to point to the preferred domain
  if (currentUrl.includes('emaxsolution.com') && !currentUrl.includes('www.emaxsolution.com')) {
    currentUrl = currentUrl.replace('://www.emaxsolution.com', '://www.emaxsolution.com')
                           .replace('://emaxsolution.com', '://www.emaxsolution.com');
  }
  // Ensure image is an absolute URL for social sharing (WhatsApp, Facebook, LinkedIn require absolute URLs)
  const absoluteImage = image ? (image.startsWith('http') ? image : `${siteUrl}${image}`) : null;

  // Infer MIME type from extension so crawlers (especially WhatsApp/Facebook) know what to fetch.
  const getImageMime = (url) => {
    if (!url) return null;
    const lower = url.split('?')[0].toLowerCase();
    if (lower.endsWith('.png')) return 'image/png';
    if (lower.endsWith('.webp')) return 'image/webp';
    if (lower.endsWith('.gif')) return 'image/gif';
    return 'image/jpeg';
  };
  const imageMime = getImageMime(absoluteImage);

  return (
    <Helmet htmlAttributes={{ lang: currentLang, dir: currentLang === 'ar' ? 'rtl' : 'ltr' }}>
      {/* Standard Meta Tags */}
      <title>{fullTitle}</title>
      {actualDescription && <meta name="description" content={actualDescription} />}
      {actualKeywords && <meta name="keywords" content={actualKeywords} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={fullTitle} />
      {actualDescription && <meta property="og:description" content={actualDescription} />}
      {absoluteImage && <meta property="og:image" content={absoluteImage} />}
      {absoluteImage && <meta property="og:image:secure_url" content={absoluteImage} />}
      {imageMime && <meta property="og:image:type" content={imageMime} />}
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content={siteName} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={fullTitle} />
      {actualDescription && <meta name="twitter:description" content={actualDescription} />}
      {absoluteImage && <meta name="twitter:image" content={absoluteImage} />}
      {absoluteImage && <meta name="twitter:image:alt" content={fullTitle} />}

      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": siteName,
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "All",
          "url": siteUrl,
          "description": actualDescription || "EMA-X solutions provides premium website and application development services.",
          "provider": {
            "@type": "Organization",
            "name": siteName,
            "url": siteUrl,
            "logo": `${siteUrl}/logoImage.webp`
          }
        })}
      </script>
    </Helmet>
  );
};

export default SEO;
